import {Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectorRef, OnDestroy} from '@angular/core';
import type { TabItem } from '@xref/xref-lib';
import {InsightsService} from "../../services/insights/insights.service";
import { OrganisationService } from '../../services/organisation/organisation.service';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

type TabId = 'metrics' | 'departments' | 'comments';

interface PulseTabItem {
  id: TabId;
  label: string;
  active: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'app-pulse-performance-tabs',
  templateUrl: './pulse-performance-tabs.component.pug',
  styleUrls: ['./pulse-performance-tabs.component.scss'],
  standalone: false
})
export class PulsePerformanceTabsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() start!: any;
  @Input() end!: any;
  @Input() request!: any;
  @Input() questionnaire!: any;
  @Input() department!: any;
  @Input() filterBy!: any;
  @Input() perfMetrics!: any[];

  activeTab: TabId = 'metrics';
  questionComments: any[] = [];
  selectedQuestionId: number | null = null;
  selectedQuestionComments: any[] = [];
  selectedQuestionText: string = '';
  orgBrandName: string = '';
  dropdownLabel: string = 'Select a question';
  dropdownOptions: any[] = [];
  paginationCriteria = { total: 0, rowsPerView: 20, currentPage: 1 };
  loadingQuestionComments = false;
  searchTitle = $localize`Sorry, comments are not available`;
  searchDescription = $localize`Not enough data to display question comments for the chosen filters.`;
  searchImg = 'https://assets.xref.com/omni/shared/seal_warning.svg';

  private organisationSubscription?: Subscription;
  private questionCommentsSubscription?: Subscription;
  private commentsSubscription?: Subscription;

  showDepartmentsTab = false;

  tabItems: PulseTabItem[] = [
    { id: 'metrics', label: $localize`Metric scores`, active: true },
    { id: 'comments', label: $localize`Question comments`, active: false }
  ];

  constructor(private insightsService: InsightsService,
              private organisationService: OrganisationService,
              private cdr: ChangeDetectorRef
             ) {}

  ngOnInit(): void {
    this.organisationSubscription = this.organisationService.getOrganisation().subscribe(
      (res) => {
        this.orgBrandName = res?.brand_name || '';
        if (this.questionComments.length > 0) this.updateDropdownOptions();
        if (this.selectedQuestionId) {
          const question = this.questionComments.find(q => q.question_id === this.selectedQuestionId);
          if (question) {
            this.dropdownLabel = this.getQuestionTextWithOrgReplaced(question.question_text);
            this.selectedQuestionText = question.question_text;
          }
        }
      },
      (err: any) => {
        console.log(err);
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['start'] || changes['end'] || changes['request'] || changes['questionnaire'] || changes['department'] || changes['filterBy']) {
      this.loadQuestionComments();
    }
  }

  setActiveTab(tab: TabId): void {
    this.activeTab = tab;
    this.syncTabItemsActiveState();
    if (tab === 'comments' && this.questionComments.length === 0) this.loadQuestionComments();
  }

  /** Add/remove "Department scores" tab when showDepartmentsTab changes; then sync active state for xref-lib. */
  private syncTabItems(): void {
    const hasDept = this.tabItems.some(t => t.id === 'departments');
    if (this.showDepartmentsTab && !hasDept) {
      this.tabItems.splice(1, 0, {
        id: 'departments',
        label: $localize`Department scores`,
        active: this.activeTab === 'departments'
      });
    } else if (!this.showDepartmentsTab && hasDept) {
      this.tabItems.splice(1, 1);
    }
    this.syncTabItemsActiveState();
  }

  private syncTabItemsActiveState(): void {
    const activeTabValid = this.tabItems.some(t => t.id === this.activeTab);
    if (!activeTabValid) {
      this.activeTab = (this.tabItems[0]?.id ?? 'metrics') as TabId;
    }
    this.tabItems.forEach(t => { t.active = t.id === this.activeTab; });
  }

  onTabClick(tab: TabItem): void {
    const id = (tab as TabItem & { id?: TabId })?.id;
    if (id) this.setActiveTab(id);
  }

  private loadQuestionComments(): void {
    this.questionCommentsSubscription?.unsubscribe();
    this.questionCommentsSubscription = this.insightsService.getPulseQuestions(
      this.start, this.end, this.request, this.department, this.questionnaire
    ).pipe(
      switchMap(questionsData => {
        this.questionComments = questionsData?.questions || [];
        this.updateDropdownOptions();

        if (this.questionComments.length > 0) {
          const question = this.questionComments[0];
          this.selectedQuestionId = question.question_id;
          this.paginationCriteria.currentPage = 1;
          this.dropdownLabel = this.getQuestionTextWithOrgReplaced(question.question_text);
          this.selectedQuestionText = question.question_text;
          return this.insightsService.getPulseQuestionComments(
            this.start, this.end, this.request, this.department, this.questionnaire,
            question.question_id, this.paginationCriteria.rowsPerView, this.paginationCriteria.currentPage
          );
        }
        this.selectedQuestionId = null;
        this.selectedQuestionComments = [];
        this.selectedQuestionText = '';
        this.dropdownLabel = 'Select a question';
        this.paginationCriteria.total = 0;
        this.paginationCriteria.currentPage = 1;
        return of(null);
      })
    ).subscribe(data => {
      if (data) {
        this.selectedQuestionComments = data?.comments || [];
        this.paginationCriteria.total = data?.total || 0;
        if (data?.question_text) this.selectedQuestionText = data.question_text;
      }
    }, error => {
      console.log(error);
    });
  }

  private loadCommentsForQuestion(questionId: number): void {
    if (!questionId) return;
    this.commentsSubscription?.unsubscribe();
    this.loadingQuestionComments = true;
    this.commentsSubscription = this.insightsService.getPulseQuestionComments(
      this.start, this.end, this.request, this.department, this.questionnaire,
      questionId, this.paginationCriteria.rowsPerView, this.paginationCriteria.currentPage
    ).subscribe(data => {
      this.selectedQuestionComments = data?.comments || [];
      this.paginationCriteria.total = data?.total || 0;
      if (data?.question_text) this.selectedQuestionText = data.question_text;
      this.loadingQuestionComments = false;
    }, error => {
      console.log(error);
      this.loadingQuestionComments = false;
    });
  }

  selectQuestion(questionId: number | string): void {
    const id = typeof questionId === 'string' ? parseInt(questionId, 10) : questionId;
    this.selectedQuestionId = id;
    this.paginationCriteria.currentPage = 1;
    this.loadCommentsForQuestion(id);
    const question = this.questionComments.find(q => q.question_id === id);
    if (question) {
      this.dropdownLabel = this.getQuestionTextWithOrgReplaced(question.question_text);
      this.selectedQuestionText = question.question_text;
    }
  }

  onPageChange(pageNumber: number): void {
    this.paginationCriteria.currentPage = pageNumber;
    this.reloadCommentsIfSelected();
  }

  onPageSizeChange(rowsPerView: number): void {
    this.paginationCriteria.rowsPerView = rowsPerView;
    this.paginationCriteria.currentPage = 1;
    this.reloadCommentsIfSelected();
  }

  private reloadCommentsIfSelected(): void {
    if (this.selectedQuestionId) this.loadCommentsForQuestion(this.selectedQuestionId);
  }

  updateDropdownOptions(): void {
    this.dropdownOptions = this.questionComments.map(q => ({
      key: q.question_id.toString(),
      value: this.getQuestionTextWithOrgReplaced(q.question_text)
    }));
  }

  onDropdownChange(event: any): void {
    if (!event?.key) return;
    const questionId = parseInt(event.key, 10);
    this.dropdownLabel = event.value;
    this.selectQuestion(questionId);
    this.cdr.detectChanges();
  }

  getLikertAnswerText(answer: string): string {
    const answerNum = parseInt(answer, 10);
    const likertMap: { [key: number]: string } = {
      0: $localize`Not applicable`,
      1: $localize`Strongly disagree`,
      2: $localize`Disagree`,
      3: $localize`Neither agree nor disagree`,
      4: $localize`Agree`,
      5: $localize`Strongly agree`
    };
    return likertMap[answerNum] || answer;
  }

  getCommentWithOrgReplaced(comment: string): string {
    if (!comment) return '';
    return comment.replace(/\{\{organisation\}\}/g, this.orgBrandName || 'the organisation');
  }

  getQuestionTextWithOrgReplaced(questionText: string): string {
    if (!questionText) return '';
    return questionText.replace(/\{\{organisation\}\}/g, this.orgBrandName || 'the organisation');
  }

  onDepartmentScoresLoaded(shouldShow: boolean): void {
    this.showDepartmentsTab = shouldShow;
    this.syncTabItems();
  }

  ngOnDestroy(): void {
    if (this.organisationSubscription) {
      this.organisationSubscription.unsubscribe();
    }
    if (this.questionCommentsSubscription) {
      this.questionCommentsSubscription.unsubscribe();
    }
    if (this.commentsSubscription) {
      this.commentsSubscription.unsubscribe();
    }
  }
}
